import {
  getAgentSessionsTokenProvider,
  type AgentSessionsTokenProvider,
} from '@github-ui/copilot-auth-token/agent-sessions-token'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {makeCAPIRequest} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {getEnv} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const DEFAULT_API_URL = 'https://api.githubcopilot.com'

interface FetchWithAuthOptions {
  path: string
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  streamingResponse?: boolean
  basePath?: string
  body?: object
  retryOn401?: boolean
  copilotApiUrl?: string | undefined
  additionalHeaders?: Record<string, string>
}

/**
 * Get a user-friendly fallback message based on HTTP status code.
 * Used when the server doesn't provide a meaningful error message.
 */
function getFallbackMessage(status: number): string {
  switch (status) {
    case 0:
      return 'Network error. Please check your connection and try again.'
    case 400:
      return 'The request was invalid. Please verify your input and try again.'
    case 401:
      return "You don't have permission to perform this action. Please sign in and try again."
    case 402:
      return "You've run out of Copilot premium quota."
    case 403:
      return "This feature isn't available for this repository or organization."
    case 404:
      return "The requested resource couldn't be found."
    case 408:
      return 'The request timed out. Please try again.'
    case 409:
      return 'A conflict occurred. The resource may already exist.'
    case 422:
      return 'The request was rejected. A repository rule may have blocked this action.'
    case 429:
      return 'Too many requests. Please wait a moment and try again.'
    default:
      return 'An unexpected error occurred. Please try again.'
  }
}

const getIntegrationId = (): string => {
  // Handle the case where this code is running in a non-browser environment (e.g. during SSR, in tests without JSDOM, etc.)
  if (typeof window === 'undefined') {
    return 'copilot-developer'
  }

  const hostname = window.location.hostname
  // If running on localhost, use the dev integration
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return 'copilot-developer-dev'
  }

  // Otherwise, use the default production integration
  return 'copilot-developer'
}

export class FetchCAPIError extends Error {
  status: number
  originalMessage: string

  constructor(message: string, status: number) {
    super(message)
    this.name = 'FetchCAPIError'
    this.status = status
    this.originalMessage = message
  }
}

export async function fetchCAPI({
  path,
  method = 'GET',
  streamingResponse = false,
  additionalHeaders,
  body,
  retryOn401,
  copilotApiUrl,
}: FetchWithAuthOptions): Promise<Response> {
  const tokenProvider = getAgentSessionsTokenProvider()
  const basePath = copilotApiUrl ?? getEnv().copilotApiOverrideUrl ?? DEFAULT_API_URL

  const shouldRetryOn401 = retryOn401 ?? isFeatureEnabled('mission_control_retry_on_401')

  return requestWithAuthRetry(
    {path, method, streamingResponse, additionalHeaders, body, basePath, retryOn401: shouldRetryOn401},
    tokenProvider,
  )
}

interface RequestWithRetryOptions {
  path: string
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  streamingResponse: boolean
  additionalHeaders?: Record<string, string>
  basePath: string
  body?: object
  retryOn401: boolean
}

const MAX_AUTH_RETRIES = 1

async function requestWithAuthRetry(
  requestOptions: RequestWithRetryOptions,
  tokenProvider: AgentSessionsTokenProvider,
  retryAttempt = 0,
): Promise<Response> {
  const token = await tokenProvider.getAuthToken()

  if (!token) {
    throw new Error('No token available')
  }

  let integrationId: string | undefined
  if (isFeatureEnabled('copilot_mission_control_always_send_integration_id')) {
    integrationId = getIntegrationId()
  }

  const response = await makeCAPIRequest({
    authToken: token,
    basePath: requestOptions.basePath,
    method: requestOptions.method,
    path: requestOptions.path,
    streamingResponse: requestOptions.streamingResponse,
    body: requestOptions.body,
    integrationId,
    additionalHeaders: requestOptions.additionalHeaders,
  })

  if (!response.ok) {
    if (requestOptions.retryOn401 && response.status === 401 && retryAttempt < MAX_AUTH_RETRIES) {
      // On 401, the access token was rejected by CAPI. Try to refresh the token first
      const refreshed = await tokenProvider.tryRefreshToken()
      if (!refreshed) {
        tokenProvider.removeLocalStorageAuthToken()
      }
      return requestWithAuthRetry(requestOptions, tokenProvider, retryAttempt + 1)
    }

    let errorMessage: string

    if (response.status >= 500) {
      errorMessage = getFallbackMessage(response.status)
    } else {
      errorMessage = 'error' in response ? response.error : getFallbackMessage(response.status)

      // Try to get the actual error message from the response body (it's plain text)
      if ('response' in response && response.response) {
        try {
          const text = await response.response.text()
          if (text && text.trim()) {
            errorMessage = text.trim()
          }
        } catch {
          // If we can't read the response body, keep the generic message
        }
      }
    }

    throw new FetchCAPIError(errorMessage, response.status)
  }

  return response
}
